import { Plus } from '@/components/_rebrand/Icons';
import cx from 'classnames';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { FC } from 'react';

export type CustomerStoryGridItemProps = {
  backgroundImage?: string;
  backgroundColor?: string;
  logo: React.ReactNode;
  title: string;
  description: string;
  href: string;
  textColor?: string;
  buttonColor?: string;
}

// Transition type use for all hover effects
const transition = {
  duration: 0.5,
  type: 'spring',
  bounce: 0,
  ease: 'easeInOut',
};
// Motion specs for animated elements.
const backgroundMotion = {
  rest: {
    scale: 1,
  },
  hover: {
    scale: 1.05,
  },
};
const textMotion = {
  rest: {
    translateY: 0,
  },
  hover: {
    translateY: -30,
  },
};
const buttonMotion = {
  rest: {
    translateY: 40,
  },
  hover: {
    translateY: 0,
  },
};
const plusMotion = {
  rest: {
    opacity: 1,
  },
  hover: {
    opacity: 0,
  },
};

export const CustomerStoryGridItem: FC<CustomerStoryGridItemProps> = ({
  backgroundImage,
  backgroundColor = 'bg-white',
  logo,
  title,
  description,
  href,
  textColor = 'text-white',
  buttonColor = 'button-primary-white',
}) => {

  return (
    // Lots of internal elements are animated on wrapper hover - using variants off wrapper's whileHover to sync animations
    <motion.a
      initial="rest"
      whileHover="hover"
      transition={transition}
      variants={{
        rest: {
          backgroundColor: '#FFFFFF00',
        },
        hover: {
          backgroundColor: '#D4B5F4',
        },
      }}
      href={href}
      className="p-1 col-span-1 block relative  rounded-[20px]"
    >
      <div
        className={`w-37 sm:min-w-38 md:min-w-min rounded-[12px] overflow-hidden relative h-full sm:w-full p-4 flex flex-col gap-4 ${textColor} ${backgroundColor} bg-cover bg-center`}
      >
        {/* Optional background image - includes a mild zoom effect on hover */}
        {backgroundImage && (
          <motion.div
            transition={transition}
            variants={backgroundImage ? backgroundMotion : undefined}
            className="absolute inset-0"
          >
            <Image src={backgroundImage} fill className="object-cover" alt="" />
          </motion.div>
        )}
        <div className={`relative z-10 flex flex-col h-full overflow-hidden `}>
          <div className="max-h-6 h-6">{logo}</div>
          {/* Text content slides up on hover */}
          <motion.div variants={textMotion} transition={transition} className="md:mb-3">
            <h3 className={`text-display1 font-500 mt-14`}>{title}</h3>
            <p className={`text-p2 `}>{description}</p>
          </motion.div>
          <div className="absolute bottom-0">
            {/* Button slides into view on hover - is  */}
            <motion.div variants={buttonMotion} transition={transition} className="mt-auto">
              <button className={cx(' block button button-size-medium', buttonColor)}>Read Customer Story</button>
            </motion.div>
          </div>
        </div>
        {/* Plus icon dissapears on hover */}
        {/* bg-current is used to match the text color of parent element */}
        <motion.div
          variants={plusMotion}
          transition={{
            duration: 0.2,
            type: 'linear',
          }}
          className={`flex bg-current absolute bottom-2 right-2 rounded-full w-[26px] h-[26px] items-center justify-center `}
        >
          <Plus strokeColor={textColor === 'text-white' ? 'stroke-blue-900' : 'stroke-white'} />
        </motion.div>
      </div>
    </motion.a>
  );
};
